import { defineStore } from "pinia";

/**
 * Abas do workspace desktop: cada rotina aberta pelo menu vira uma aba no centro.
 * A home ("/") não fecha; as demais podem ser fechadas com o X.
 */
export interface DesktopTab {
	/** Identificador estável — usa o path normalizado. */
	id: string;
	path: string;
	label: string;
	/** Home permanece fixa. */
	closable: boolean;
}

function normalizarPath(path: string): string {
	if (!path || path === "") return "/";
	// Remove query/hash e barras finais (exceto a raiz).
	const semQuery = path.split("?")[0]?.split("#")[0] ?? "/";
	if (semQuery === "/") return "/";
	return semQuery.replace(/\/+$/, "") || "/";
}

export const useDesktopTabsStore = defineStore("desktopTabs", () => {
	const tabs = ref<DesktopTab[]>([
		{
			id: "/",
			path: "/",
			label: "Painel operacional",
			closable: false,
		},
	]);
	const activePath = ref("/");

	function abrir(path: string, label?: string) {
		const p = normalizarPath(path);
		const existente = tabs.value.find(t => t.path === p);
		if (!existente) {
			tabs.value.push({
				id: p,
				path: p,
				label: label?.trim() || tituloFallback(p),
				closable: p !== "/",
			});
		}
		else if (label?.trim() && existente.label !== label.trim()) {
			// Atualiza rótulo se o menu trouxe um nome melhor.
			existente.label = label.trim();
		}
		activePath.value = p;
	}

	function ativar(path: string) {
		const p = normalizarPath(path);
		const tab = tabs.value.find(t => t.path === p);
		if (!tab) return;
		activePath.value = p;
		void navigateTo(p);
	}

	function fechar(path: string) {
		const p = normalizarPath(path);
		const idx = tabs.value.findIndex(t => t.path === p);
		if (idx < 0) return;
		if (!tabs.value[idx].closable) return;

		const eraAtiva = activePath.value === p;
		tabs.value.splice(idx, 1);

		if (eraAtiva) {
			const vizinha = tabs.value[Math.max(0, idx - 1)] ?? tabs.value[0];
			if (vizinha) ativar(vizinha.path);
		}
	}

	function tituloFallback(path: string): string {
		if (path === "/") return "Painel operacional";
		const trecho = path.split("/").filter(Boolean).pop() ?? path;
		return trecho;
	}

	return {
		tabs,
		activePath,
		abrir,
		ativar,
		fechar,
		normalizarPath,
	};
});
